import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import useLogout from '../Auth/useLogout';
import Dataloader from '../../Component/Dataloader';
import NoData from '../../Component/NoData';
import Pagination from '../../Component/Pagination';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

export default function StaffList() {
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStaff, setTotalStaff] = useState(0);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const logout = useLogout();

  // Fetch staff data from API
  const fetchStaffData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.apiUrl}/staff?page=${page}`, {
        headers: {
          'Authorization': config.token,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setStaffList(response.data.data.data);
        setCurrentPage(response.data.data.current_page);
        setTotalPages(response.data.data.last_page);
        setTotalStaff(response.data.data.total);
      } else {
        toast.error(response.data.message || 'Failed to fetch staff data');
      }
    } catch (error) {
      console.error('Error fetching staff data:', error);
      if (error.response?.status === 401) {
        logout();
      } else {
        toast.error('Failed to load staff data');
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    fetchStaffData(currentPage);
  }, [currentPage, fetchStaffData]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle navigate to add staff
  const handleAddStaff = () => {
    navigate('/staff/create');
  };

  // Handle navigate to edit staff
  const handleEditStaff = (staffId) => {
    navigate(`/staff/edit/${staffId}`);
  };

  // Helper function to get active permissions for a staff member
  const getActivePermissions = (permissions) => {
    if (!permissions || !permissions[0]) return [];
    
    const activePerms = [];
    const permissionObj = permissions[0];
    
    Object.keys(permissionObj).forEach(category => {
      Object.keys(permissionObj[category]).forEach(permission => {
        if (permissionObj[category][permission] === true) {
          activePerms.push({
            category: category.charAt(0).toUpperCase() + category.slice(1),
            permission: permission.charAt(0).toUpperCase() + permission.slice(1)
          });
        }
      });
    });
    
    return activePerms;
  };

  // Handle show permissions modal
  const handleShowPermissions = (staff) => {
    setSelectedStaff(staff);
    setShowPermissionsModal(true);
  };

  // Handle close permissions modal
  const handleClosePermissionsModal = () => {
    setShowPermissionsModal(false);
    setSelectedStaff(null);
  };

  // Handle delete staff
  const handleDeleteStaff = async (staff) => {
    // Show SweetAlert2 confirmation dialog
    const result = await Swal.fire({
      title: 'Delete Staff',
      html: `
        <div class="text-start">
          <p class="mb-3">Are you sure you want to delete this staff?</p>
          <div class="alert alert-warning">
            <div class="d-flex align-items-center mb-2">
              <div class="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" 
                   style="width: 35px; height: 35px;">
                <span class="text-white fw-bold small">${staff.name.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <div class="fw-semibold">${staff.name}</div>
                <div class="text-muted small">${staff.email}</div>
              </div>
            </div>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-trash me-1"></i> Yes, Delete',
      cancelButtonText: '<i class="fas fa-times me-1"></i> Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      customClass: {
        confirmButton: 'swal2-confirm-custom',
        cancelButton: 'swal2-cancel-custom'
      },
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await axios.delete(`${config.apiUrl}/staff/${staff.id}`, {
          headers: {
            'Authorization': config.token,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          // Show success alert
          await Swal.fire({
            title: 'Deleted!',
            text: `${response.data.message || 'Staff has been deleted successfully.'}`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745',
            customClass: {
              confirmButton: 'swal2-success-custom'
            }
          });

          // Refresh the staff list
          fetchStaffData(currentPage);
        } else {
          throw new Error(response.data.message || 'Failed to delete staff. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting staff:', error);
        
        await Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to delete staff. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
          customClass: {
            confirmButton: 'swal2-error-custom'
          }
        });

        if (error.response?.status === 401) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return <Dataloader />;
  }

  return (
    <div className="container-fluid p-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold text-dark mb-1">
                <i className="fas fa-users me-2 text-primary"></i>
                Staff Management
              </h2>
              <p className="text-muted mb-0">Manage your team members and their permissions</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="bg-light rounded-pill px-3 py-2">
                <i className="fas fa-chart-bar me-2 text-info"></i>
                <span className="fw-semibold text-dark">Total Staff: </span>
                <span className="badge bg-primary ms-1">{totalStaff}</span>
              </div>
              <button 
                className="btn btn-primary btn-sm rounded-pill"
                onClick={handleAddStaff}
              >
                <i className="fas fa-plus me-1"></i>
                Add Staff
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              {staffList.length === 0 ? (
                <div className="p-5">
                  <NoData />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0 py-3 fw-semibold text-dark">
                            <i className="fas fa-user me-2 text-muted"></i>Staff
                          </th>
                          <th className="border-0 py-3 fw-semibold text-dark">
                            <i className="fas fa-envelope me-2 text-muted"></i>Contact Info
                          </th>
                          <th className="border-0 py-3 fw-semibold text-dark">
                            <i className="fas fa-envelope me-2 text-muted"></i>Role
                          </th>
                          <th className="border-0 py-3 fw-semibold text-dark">
                            <i className="fas fa-shield-alt me-2 text-muted"></i>Permissions
                          </th>
                          <th className="border-0 py-3 fw-semibold text-dark text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffList.map((staff) => (
                          <tr key={staff.id} className="border-bottom">
                            <td className="py-3">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" 
                                     style={{width: '40px', height: '40px'}}>
                                  <span className="text-white fw-bold">
                                    {staff.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">{staff.name}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3">
                              <div>
                                <div className="text-dark mb-1">
                                  <i className="fas fa-envelope me-1 text-muted"></i>
                                  {staff.email}
                                </div>
                                <div className="text-muted small">
                                  <i className="fas fa-phone me-1"></i>
                                  {staff.phone}
                                </div>
                              </div>
                            </td>
                            <td className="py-3">
                              {staff.role || <span className="text-muted">N/A</span>}
                            </td>
                            <td className="py-3">
                              <div className="d-flex align-items-center">
                                {getActivePermissions(staff.permission).length > 0 ? (
                                  <button 
                                    className="btn btn-outline-info btn-sm rounded-pill px-3"
                                    onClick={() => handleShowPermissions(staff)}
                                    title="View Permissions"
                                  >
                                    <i className="fas fa-shield-alt me-1"></i>
                                    View Permissions ({getActivePermissions(staff.permission).length})
                                  </button>
                                ) : (
                                  <span className="text-muted small">
                                    <i className="fas fa-ban me-1"></i>
                                    No permissions
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <button 
                                  className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                  title="Edit Staff"
                                  onClick={() => handleEditStaff(staff.id)}
                                >
                                  <i className="fas fa-edit me-1"></i>
                                  Edit
                                </button>
                                <button 
                                  className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                  title="Delete Staff"
                                  onClick={() => handleDeleteStaff(staff)}
                                >
                                  <i className="fas fa-trash me-1"></i>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {totalPages > 1 && (
                    <div className="p-4 bg-light border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          Showing {staffList.length} of {totalStaff} staff members
                        </div>
                        <Pagination 
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Permissions Modal */}
      {showPermissionsModal && selectedStaff && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-shield-alt me-2"></i>
                  Staff Permissions - {selectedStaff.name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-dark" 
                  onClick={handleClosePermissionsModal}
                ></button>
              </div>
              <div className="modal-body p-0">
                <div className="p-4">
                  {/* Staff Info */}
                  <div className="d-flex align-items-center mb-4 p-3 bg-light rounded">
                    <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" 
                         style={{width: '50px', height: '50px'}}>
                      <span className="text-white fw-bold fs-5">
                        {selectedStaff.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-semibold">{selectedStaff.name}</h6>
                      <p className="text-muted mb-0 small">
                        <i className="fas fa-envelope me-1"></i>
                        {selectedStaff.email} | 
                        <i className="fas fa-phone ms-2 me-1"></i>
                        {selectedStaff.phone}
                      </p>
                    </div>
                  </div>

                  {/* Permissions */}
                  {selectedStaff.permission && selectedStaff.permission[0] ? (
                    <div className="row">
                      {/* Product Permissions */}
                      {selectedStaff.permission[0].product && config.user.type === 'seller' && (
                        <div className="col-md-6 mb-4">
                          <div className="card border-primary h-100">
                            <div className="card-header bg-primary text-white py-2">
                              <h6 className="mb-0">
                                <i className="fas fa-box me-2"></i>
                                Product Management
                              </h6>
                            </div>
                            <div className="card-body">
                              {Object.keys(selectedStaff.permission[0].product).map(permission => (
                                <div key={permission} className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="text-capitalize">{permission}</span>
                                  <span className={`badge ${selectedStaff.permission[0].product[permission] ? 'bg-success' : 'bg-secondary'}`}>
                                    <i className={`fas ${selectedStaff.permission[0].product[permission] ? 'fa-check' : 'fa-times'} me-1`}></i>
                                    {selectedStaff.permission[0].product[permission] ? 'Allowed' : 'Denied'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Quotation Permissions */}
                      {selectedStaff.permission[0].quotation && (
                        <div className="col-md-6 mb-4">
                          <div className="card border-success h-100">
                            <div className="card-header bg-success text-white py-2">
                              <h6 className="mb-0">
                                <i className="fas fa-file-invoice me-2"></i>
                                Quotation Management
                              </h6>
                            </div>
                            <div className="card-body">
                              {Object.keys(selectedStaff.permission[0].quotation).map(permission => (
                                <div key={permission} className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="text-capitalize">{permission}</span>
                                  <span className={`badge ${selectedStaff.permission[0].quotation[permission] ? 'bg-success' : 'bg-secondary'}`}>
                                    <i className={`fas ${selectedStaff.permission[0].quotation[permission] ? 'fa-check' : 'fa-times'} me-1`}></i>
                                    {selectedStaff.permission[0].quotation[permission] ? 'Allowed' : 'Denied'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order Permissions */}
                      {selectedStaff.permission[0].order && (
                        <div className="col-md-6 mb-4">
                          <div className="card border-info h-100">
                            <div className="card-header bg-info text-white py-2">
                              <h6 className="mb-0">
                                <i className="fas fa-shopping-cart me-2"></i>
                                Order Management
                              </h6>
                            </div>
                            <div className="card-body">
                              {Object.keys(selectedStaff.permission[0].order).map(permission => (
                                <div key={permission} className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="text-capitalize">{permission}</span>
                                  <span className={`badge ${selectedStaff.permission[0].order[permission] ? 'bg-success' : 'bg-secondary'}`}>
                                    <i className={`fas ${selectedStaff.permission[0].order[permission] ? 'fa-check' : 'fa-times'} me-1`}></i>
                                    {selectedStaff.permission[0].order[permission] ? 'Allowed' : 'Denied'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Wallet Permissions */}
                      {selectedStaff.permission[0].wallet && (
                        <div className="col-md-6 mb-4">
                          <div className="card border-warning h-100">
                            <div className="card-header bg-warning text-dark py-2">
                              <h6 className="mb-0">
                                <i className="fas fa-wallet me-2"></i>
                                Wallet Management
                              </h6>
                            </div>
                            <div className="card-body">
                              {Object.keys(selectedStaff.permission[0].wallet).map(permission => (
                                <div key={permission} className="d-flex justify-content-between align-items-center mb-2">
                                  <span>{permission.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                  <span className={`badge ${selectedStaff.permission[0].wallet[permission] ? 'bg-success' : 'bg-secondary'}`}>
                                    <i className={`fas ${selectedStaff.permission[0].wallet[permission] ? 'fa-check' : 'fa-times'} me-1`}></i>
                                    {selectedStaff.permission[0].wallet[permission] ? 'Allowed' : 'Denied'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-ban text-muted" style={{fontSize: '3rem'}}></i>
                      <h5 className="text-muted mt-3">No Permissions Assigned</h5>
                      <p className="text-muted">This staff member has no permissions assigned yet.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-outline-primary rounded-pill px-4"
                  onClick={() => {
                    handleClosePermissionsModal();
                    handleEditStaff(selectedStaff.id);
                  }}
                >
                  <i className="fas fa-edit me-1"></i>
                  Edit Permissions
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary rounded-pill px-4" 
                  onClick={handleClosePermissionsModal}
                >
                  <i className="fas fa-times me-1"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
